// Search-tag helpers split from ConstructionStatePage.tsx so route head() code
// does not pull the full page layout into the shared first-load bundle.
import type { StateInfo } from "@/data/states";
import { isLicensedState } from "@/data/states";

export function stateFaq(state: StateInfo): { q: string; a: string }[] {
  return [
    {
      q: `Do you fund ground-up construction loans in ${state.name}?`,
      a: `Yes. Business-purpose construction loans are available across ${state.name} for 1–4 unit residential builds — up to 85% LTC and 70% ARV, with a built-in DSCR takeout at certificate of occupancy.`,
    },
    {
      q: `How long does a typical ${state.name} build take from close to C/O?`,
      a: `Most single-family ground-up builds in ${state.name} run 8–10 months from ground-break to certificate of occupancy. Add 30–60 days for the DSCR refinance if you're holding it as a rental.`,
    },
    {
      q: `How much cash do I need to close a ${state.name} construction loan?`,
      a: `Plan on roughly 15–25% of total project cost at close: land equity (or the difference between purchase price and 85% LTC) plus soft costs, closing costs, and 6 months of PITIA reserves.`,
    },
    {
      q: `Can I be my own general contractor in ${state.name}?`,
      a: `Owner-builder is considered case-by-case in ${state.name}. It typically requires documented prior build experience, a lower max LTC (75–80%), and additional post-close liquidity. Licensed third-party GC files get materially better pricing and leverage.`,
    },
    {
      q: `What ${state.name} permits and approvals are required before we close?`,
      a: `Approved building permits or a written permit-ready letter from the local jurisdiction, stamped plans, a signed GC contract with a schedule of values, and evidence of paid impact/water/sewer taps where applicable.`,
    },
    {
      q: `Does the ${state.name} construction loan cover land purchase?`,
      a: `Yes. Land is funded at closing as part of the loan (subject to LTC caps). If you already own the land, its appraised value is credited toward your down payment and can reduce cash-in materially.`,
    },
    {
      q: `Is there a DSCR takeout available in ${state.name}?`,
      a: `Yes — the same underwriter closes a 30-year DSCR permanent loan at C/O, reusing the file and appraisal. Takeout typically funds in 10–15 business days.`,
    },
    {
      q: `Are ${state.name} construction loans business-purpose?`,
      a: `${isLicensedState(state.slug) ? "Yes — every construction loan we originate in " + state.name + " is a business-purpose loan on non-owner-occupied investment property, closed in your LLC, LP, or corporate entity." : "Yes. In " + state.name + " these are business-purpose loans vested in an LLC or corporate entity, exempt from consumer disclosure requirements (RESPA/TILA/TRID)."}`,
    },
  ];
}

export function constructionStateFaqJsonLd(state: StateInfo) {
  const items = stateFaq(state);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}

export function constructionStateMetaDescription(state: StateInfo): string {
  return `Ground-up construction loans in ${state.name}: up to 85% LTC / 70% ARV, 2-week draws, land included, DSCR takeout at C/O. Active in ${state.topMarkets.slice(0, 3).join(", ")}. NMLS #2620881.`.slice(0, 158);
}
